import { Button, ButtonProps } from '@mui/material';
import { DynamicDefaultProps } from '@synergy/utils';
import { PoolDynamicDefaultPropsComponent } from '@synergy/abstract';
import { Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';

// The shop and the customer app render this button under themes that predate
// the contract, so the values it always had stand in when it is missing.
const LEGACY_BUTTON = { minHeight: 38, borderRadius: 4, fontSize: 13 };

const styles = (theme: Theme) =>
    createStyles({
        root: {
            minHeight: (theme.button ?? LEGACY_BUTTON).minHeight,
            borderRadius: (theme.button ?? LEGACY_BUTTON).borderRadius,
            textTransform: 'none',
        },
        disabledText: {
            color: theme.palette.text.disabled,
            opacity: 1,
            '& svg': {
                color: theme.palette.text.disabled,
            },
        },
        disabledBackground: {
            boxShadow: 'none',
            backgroundColor: theme.palette.grey[100],
        },
        // Sentence case, matching the PALO buttons next to them.
        firstLevel: {
            fontSize: (theme.button ?? LEGACY_BUTTON).fontSize,
            textTransform: 'none',
            fontWeight: 560,
            letterSpacing: 'normal',
            lineHeight: '115%',
        },
        secondLevel: {
            '&:hover': {
                backgroundColor: theme.palette.grey[100],
            },
        },
        thirdLevel: {
            borderRadius: theme.spacing(3),
            color: theme.palette.text.primary,
            '&:disabled': {
                backgroundColor: 'transparent',
            },

            '&:hover': {
                backgroundColor: theme.palette.common.white,
                boxShadow: '0 2px 16px 0 rgba(0,0,0,0.5)', // magic
            },
        },
        link: {
            '&:hover': {
                backgroundColor: theme.palette.common.white,
                boxShadow: '0 2px 26px 0 rgba(0, 0, 0, .5)',
            },

            '&:not([disabled])': {
                borderRadius: '2rem',
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.grey[100],

                '& .material-icons': {
                    color: theme.palette.primary.main,
                },
            },

            '& .material-icons:first-child': {
                marginRight: theme.spacing(),
            },
        },
        // Made for the dark header the shop and the customer app still draw.
        // The admin screens put it in a white PALO header instead, where its
        // own 15-pixel type in 23 pixels of padding stood beside 12.5-pixel
        // buttons — so where the contract says what a button is, it says it
        // here too.
        toolbar: {
            color: theme.toolbar.color,
            ...(theme.button
                ? {
                      fontSize: theme.button.fontSize,
                      borderRadius: theme.button.borderRadius,
                      padding: '4px 12px',
                      lineHeight: '115%',
                  }
                : {
                      fontSize: 15,
                      borderRadius: 0,
                      padding: '0 23px',
                      lineHeight: '145%',
                  }),
            minWidth: 48,

            '&:disabled, &:disabled svg': {
                color: theme.toolbar.disabledColor,
            },

            '&:hover': {
                backgroundColor: theme.toolbar.hoverBackgroundColor,
            },

            '& .material-icons': {
                marginRight: theme.spacing(2),
            },

            '& svg': {
                fontSize: theme.button ? 18 : 20,
            },

            '&.poolButtonEmpty': {
                padding: 0,
                '& .MuiButton-startIcon': {
                    margin: 0,
                },
            },
        },
        // A borderless button inside a heading row. Its own 24 was written for
        // 38-pixel buttons around it; beside PALO's 30 it read as a smaller
        // control than everything it sits with.
        header: {
            minHeight: theme.button ? theme.button.minHeight : 24,
            padding: 0,

            '& > span > span.MuiButton-startIcon': {
                marginRight: theme.spacing(),
            },
        },
        // The lozenge the legacy overview bar draws. In a PALO header it stood
        // 40 tall and fully rounded beside 30-pixel square-cornered buttons.
        filter: theme.button
            ? {
                  height: theme.button.minHeight,
                  padding: '4px 12px',
                  borderRadius: theme.button.borderRadius,
              }
            : {
                  height: 40,
                  padding: '10px 22px 10px 16px',
                  borderRadius: 24,
              },
    });

interface PoolButtonBaseProps extends WithStyles<typeof styles> {
    styleType?: keyof ReturnType<typeof styles>;
    // Target property of ButtonProps is not correctly found, needed to redefine here
    target?: string;
}

type PoolButtonProps = PoolButtonBaseProps & ButtonProps;

export const PoolButton = withStyles(styles)(
    class PoolButtonBase extends PoolDynamicDefaultPropsComponent<PoolButtonProps> {
        protected override dynamicDefaultProps: DynamicDefaultProps<PoolButtonProps> = {
            firstLevel: {
                color: 'primary',
                variant: 'contained',
            },
            secondLevel: {
                color: 'primary',
            },
            thirdLevel: {},
            link: {
                color: 'primary',
            },
            header: {},
        };

        private static getDisplayClass(props: PoolButtonProps): string {
            let className = '';
            if (props.styleType) {
                className = props.classes[props.styleType];
                if (!props.children) {
                    className = className + ' poolButtonEmpty';
                }
            }

            return className;
        }

        private static getClassNames(props: PoolButtonProps) {
            return `${this.getDisplayClass(props)} ${props.classes.root} ${props.className || ''}`;
        }

        public override render() {
            const { defaultProps } = this.state;
            const { styleType: _styleType, classes, ...baseButtonProps } = this.props;
            const props = { ...defaultProps, ...this.props };

            const disabled =
                props.variant === 'contained'
                    ? `${classes.disabledBackground} ${classes.disabledText}`
                    : classes.disabledText;

            return (
                <Button
                    {...defaultProps}
                    {...baseButtonProps}
                    classes={{ disabled }}
                    className={PoolButtonBase.getClassNames(props)}
                >
                    {props.children}
                </Button>
            );
        }
    }
);
